'use client';

import { Col, Collapse, ConfigProvider, Flex, Row } from 'antd';
import 'bootstrap-icons/font/bootstrap-icons.min.css';
import React, { useState } from 'react';

import styles from './content-card.styles';

/**
 * Props para el componente ContentCard.
 */
type ContentCardProps = {
  /**
   * El nombre del cliente.
   */
  customerName?: string;
  /**
   * El número de teléfono del cliente.
   * Este campo solo es visible cuando la tarjeta está expandida.
   */
  phoneNumber?: string;
  /**
   * La dirección de correo electrónico del cliente.
   * Este campo solo es visible cuando la tarjeta está expandida.
   */
  email?: string;
  /**
   * La dirección de entrega para el cliente.
   */
  deliveryAddress?: string;
};

/**
 * Un componente de tarjeta para mostrar información del cliente con una sección expandible
 * para detalles de contacto como teléfono y correo electrónico.
 *
 * @param {ContentCardProps} props - Las props para el componente ContentCard.
 *
 * @returns {JSX.Element} El componente ContentCard renderizado.
 */
const ContentCard = ({
  customerName,
  phoneNumber,
  email,
  deliveryAddress
}: Readonly<ContentCardProps>) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleCollapseChange = (key: string | string[]) => {
    setIsExpanded(key.length > 0);
  };

  return (
    <Flex vertical style={styles.contentCard}>
      <Row gutter={[0, 12]}>
        <Col className='font-light' span={24} style={styles.contentCard__col}>
          Cliente
          <p style={styles.contentCard__value}>{customerName}</p>
        </Col>

        <Col className='font-light' span={24} style={styles.contentCard__col}>
          Dirección de entrega
          <p style={styles.contentCard__value}>{deliveryAddress}</p>
        </Col>

        {/* Mostrar Contacto y Email solo si está expandido */}
        {isExpanded && (
          <>
            <Col className='font-light' span={24} style={styles.contentCard__col}>
              Contacto
              <p style={styles.contentCard__value}>{phoneNumber}</p>
            </Col>

            <Col className='font-light' span={24} style={styles.contentCard__col}>
              Email
              <p style={styles.contentCard__value}>{email}</p>
            </Col>
          </>
        )}

        <ConfigProvider
          theme={{
            components: {
              Collapse: {
                headerBg: 'transparent',
                headerPadding: 0
              }
            }
          }}>
          <Collapse
            bordered={false}
            onChange={handleCollapseChange}
            expandIconPosition='end'
            ghost
            items={[
              {
                label: (
                  <span style={styles.contentCard__showmore} className='font-semibold'>
                    {isExpanded ? 'Ver menos' : 'Ver más'}
                  </span>
                ),
                showArrow: false
              }
            ]}></Collapse>
        </ConfigProvider>
      </Row>
    </Flex>
  );
};

export default ContentCard;
